// Wizard de création de dossier TEF (CC-DEV1-31) — vrai stepper 4 étapes :
// Bénéficiaire → Logement → MaPrimeAdapt' → Documents.
// SOCLE générique (réutilise BanAddressInput partagé + ClientDuplicateDialog + endpoints
// dedup/clients/dossiers existants). Validation NON bloquante (on avance avec des champs
// vides ; seuls les champs vraiment obligatoires bloquent le bouton Suivant).
// Source unique : client → metier_clients (POST/PATCH), dossier → metier_dossiers.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { ArrowLeft, ArrowRight, Save, Loader2, Check, Upload, User, Home, HeartHandshake, FileText, Sparkles, Wallet } from "lucide-react";
import BanAddressInput from "../../components/shared/BanAddressInput";
// 🔑 SOURCE UNIQUE de lecture des listes tenant — ⛔ ne pas refaire un fetch ici.
import { useConfigList } from "../../hooks/useConfigList";

import ClientDuplicateDialog from "./dossier/ClientDuplicateDialog";
import { enteteTenant } from "../../utils/enteteTenant";
import ChampDate from "../../components/ui/champ-date";

// 🔴 RÈGLE N°745 — ⛔ « SYNDIC » N'EST PAS DANS CE REPLI. Il entre par la liste du TENANT
//    (`metier_config_listes{type:"client_types"}`, migration 061), ⛔ jamais par le code :
//    une valeur nouvelle écrite ici serait une 2e autorité, invisible de l'écran de config.
// 🔑 Ce repli n'est qu'un FILET DE NON-RÉGRESSION : uniquement les valeurs déjà PORTÉES par
//    les données (mesuré 2026-09-27 : `professionnel` 151 clients PROD, `particulier` 91).
const TYPE_CLIENT_REPLI = [
  { value: "particulier", label: "Particulier" },
  { value: "professionnel", label: "Professionnel" },
  { value: "entreprise", label: "Entreprise" },
];

const API = process.env.REACT_APP_BACKEND_URL || "";
const lbl = { color: "var(--oh-text-muted)" };
const inputStyle = { background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)", color: "var(--oh-text)" };

function Txt({ label, value, onChange, type = "text", placeholder, testId }) {
  return (
    <div>
      <label className="text-xs mb-1 block" style={lbl}>{label}</label>
      {type === "date"
        ? <ChampDate value={value ?? ""} onChange={(e) => onChange(e.target.value)}
            data-testid={testId} className="w-full rounded px-3 py-2 text-sm" style={inputStyle} />
        : <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder || "À remplir"}
        data-testid={testId} className="w-full rounded px-3 py-2 text-sm" style={inputStyle} />}
    </div>
  );
}
function Sel({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="text-xs mb-1 block" style={lbl}>{label}</label>
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="w-full rounded px-3 py-2 text-sm" style={inputStyle}>
        <option value="">À remplir</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

// 🔴 LE CATALOGUE DES ÉTAPES — une étape = une entrée, ⛔ pas une condition.
const CATALOGUE_ETAPES = {
  beneficiaire: { key: "beneficiaire", label: "Bénéficiaire", icon: User },
  logement:     { key: "logement", label: "Logement", icon: Home },
  // 🔑 CLI-16 — « REVENUS DU FOYER » EST UNE ÉTAPE À PART, et c'est tout le
  //    point. ⚠️ `rfr` et `nb_personnes_foyer` vivaient DANS l'étape
  //    « MaPrimeAdapt' » : masquer MPA (ce que fait `installateur`) les
  //    EMPORTAIT. Or ils ⛔ ne sont PAS de l'AMO — ils serviront à
  //    MaPrimeRénov' en B2C. ⇒ Les revenus se déclarent SÉPARÉMENT de
  //    l'éligibilité MPA, sinon tout parcours non-AMO perd la ressource.
  revenus:      { key: "revenus", label: "Revenus du foyer", icon: Wallet },
  mpa:          { key: "mpa", label: "MaPrimeAdapt'", icon: HeartHandshake },
  documents:    { key: "documents", label: "Documents", icon: FileText },
};

// 🔴 LES PARCOURS, DÉCLARÉS — ⛔ PLUS DE RÈGLE EN CREUX.
//
// ⚠️ CE QUE ÇA REMPLACE : `profile === "installateur" ? sans MPA : TOUT`. Une
//    règle « tout sauf X » change de sens le jour où un X de plus apparaît, et
//    ⛔ personne ⛔ ne relit la condition ce jour-là. C'est ce qui aurait donné
//    MaPrimeAdapt' à Climat.
// 🔑 Un parcours ÉNUMÈRE ses étapes. Ajouter un métier = ajouter une LIGNE,
//    ⛔ pas modifier un test.
//
//   · `tef_amo`      — l'AMO ANAH complet (⛔ INCHANGÉ : mêmes étapes qu'avant).
//   · `installateur` — flux réduit (CC-DEV1), ⛔ sans éligibilité MPA.
//   · `b2c`          — CLI-16 : particulier GÉNÉRIQUE, ⛔ PAS propre à Climat.
//                      Bénéficiaire + logement + REVENUS + documents.
//                      ⛔ SANS MaPrimeAdapt', GIR, APA, PCH, pièces ANAH,
//                      visite AMO. Les revenus RESTENT : ils serviront à MPR.
const PARCOURS_ETAPES = {
  tef_amo:      ["beneficiaire", "logement", "mpa", "documents"],
  installateur: ["beneficiaire", "logement", "documents"],
  b2c:          ["beneficiaire", "logement", "revenus", "documents"],
};
//: ⚠️ Repli HISTORIQUE assumé : avant CLI-16, `profile` arrivait à `null` et
//:    l'écran servait le parcours AMO complet. On le CONSERVE pour ⛔ ne RIEN
//:    changer à TEF — le fail-closed sur le parcours non déclaré vit en amont,
//:    dans `CreateDossier.jsx`, ⛔ pas ici.
const PARCOURS_DEFAUT = "tef_amo";

export default function CreateDossierTefWizard({ tenantId, activite, civiliteOptions = [], onBack, onSuccess, profile = null }) {
  const STEPS = (PARCOURS_ETAPES[profile] || PARCOURS_ETAPES[PARCOURS_DEFAUT])
    .map((k) => CATALOGUE_ETAPES[k]);
  const [step, setStep] = useState(0);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  // Bénéficiaire : nouveau OU existant
  const [clientMode, setClientMode] = useState("nouveau");   // "nouveau" | "existant"
  const [existingClient, setExistingClient] = useState(null);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [benef, setBenef] = useState({ civilite: "", nom: "", prenom: "", email: "", telephone: "", date_naissance: "" });
  // 🔴 N°744 — LE TYPE DE CLIENT SE CHOISIT À LA CRÉATION. Avant ce lot, ce wizard écrivait
  // `type_client: "particulier"` EN DUR (aucun choix) : un SYNDIC était donc créé comme
  // particulier, et c'est ce que JJ a vu manquer à l'écran.
  // ⚠️ L'autorité de la liste est le tenant (`metier_config_listes{type:"client_types"}`),
  //    lue par `useConfigList` — ⛔ pas une 5e liste en dur. Repli si non seedée.
  // 🔴 N°745 : ⛔ PLUS AUCUN littéral `"particulier"` ici. Le défaut vient de la LISTE DU
  //    TENANT (item `default:true`, sinon le premier) — le tenant décide donc aussi de ce
  //    qui est proposé par défaut. Tant que rien n'est chargé, la valeur reste vide et
  //    l'utilisateur CHOISIT : ⛔ aucun syndic créé « particulier » par inadvertance.
  const [typeClient, setTypeClient] = useState("");
  const { items: clientTypeItems } = useConfigList("client_types");
  const typeClientOptions = clientTypeItems.length
    ? clientTypeItems.map((o) => ({ value: o.id, label: o.label }))
    : TYPE_CLIENT_REPLI;
  useEffect(() => {
    if (typeClient) return;                       // ⛔ ne JAMAIS écraser un choix fait
    const parDefaut = clientTypeItems.find((o) => o.default)?.id
      || clientTypeItems[0]?.id || TYPE_CLIENT_REPLI[0].value;
    setTypeClient(parDefaut);
  }, [clientTypeItems, typeClient]);
  // 🔴 N°744 — UN SYNDIC GÈRE PLUSIEURS ADRESSES, et il les a DÈS la création.
  // ⚠️ Elles sont postées APRÈS la création du dossier (la route adresses exige un
  //    dossier_id) : ⛔ aucune adresse n'est perdue si le dossier échoue, puisqu'il n'existe
  //    pas encore. L'adresse du LOGEMENT reste la principale — ⛔ elle ⛔ n'est PAS dupliquée ici.
  const [adressesSup, setAdressesSup] = useState([]);
  const [logement, setLogement] = useState({ adresse: "", code_postal: "", ville: "", type_logement: "", surface_habitable: "", statut_occupation: "" });
  const [mpa, setMpa] = useState({ gir: "", rfr: "", nb_personnes_foyer: "", age: "", beneficiaire_apa: false, beneficiaire_pch: false });

  // Documents (pré-remplissage IA)
  const [extracting, setExtracting] = useState(false);
  const [suggestions, setSuggestions] = useState(null);   // {champ: valeur} proposées
  const [docName, setDocName] = useState("");

  const [dupDialog, setDupDialog] = useState({ open: false, matches: [], onUse: null, onCreate: null });

  const headers = () => {
    const t = localStorage.getItem("session_token");
    return { ...enteteTenant(tenantId), "Content-Type": "application/json", ...(t && { Authorization: `Bearer ${t}` }) };
  };

  // ── Validation NON bloquante : seul l'obligatoire bloque le « Suivant ». ──
  // Indexée par CLÉ d'étape (robuste aux profils qui retirent des étapes).
  const stepRequiredMissing = (i) => {
    const key = STEPS[i]?.key;
    if (key === "beneficiaire") return clientMode === "existant" ? (existingClient ? [] : ["sélectionner un client"]) : (benef.nom.trim() ? [] : ["nom du bénéficiaire"]);
    return []; // Logement / MPA / Documents : tout est optionnel (complétable plus tard)
  };
  // Compteur « X champs à remplir » (optionnels vides de l'étape courante).
  const emptyCount = (i) => {
    const key = STEPS[i]?.key;
    const empty = (o, keys) => keys.filter((k) => !String(o[k] ?? "").trim()).length;
    if (key === "beneficiaire") return clientMode === "existant" ? 0 : empty(benef, ["civilite", "nom", "prenom", "email", "telephone", "date_naissance"]);
    if (key === "logement") return empty(logement, ["adresse", "code_postal", "ville", "type_logement", "surface_habitable", "statut_occupation"]);
    if (key === "mpa") return empty(mpa, ["gir", "rfr", "nb_personnes_foyer", "age"]);
    if (key === "revenus") return empty(mpa, ["rfr", "nb_personnes_foyer"]);
    return 0;
  };

  const missing = stepRequiredMissing(step);
  const canNext = missing.length === 0;

  const doSearch = async (q) => {
    setSearch(q);
    if (q.length < 2) { setResults([]); return; }
    try {
      const r = await fetch(`${API}/api/metier/clients/search?q=${encodeURIComponent(q)}&limit=5`, { headers: headers(), credentials: "include" });
      if (r.ok) setResults(await r.json());
    } catch { /* noop */ }
  };

  // Pré-remplissage IA : dépose un document → l'IA SUGGÈRE (n'écrit pas en dur).
  const onUploadDoc = async (file) => {
    if (!file) return;
    setDocName(file.name); setExtracting(true); setSuggestions(null);
    try {
      const t = localStorage.getItem("session_token");
      const fd = new FormData(); fd.append("file", file);
      const r = await fetch(`${API}/api/metier/dossiers/extract-beneficiaire`, {
        method: "POST", credentials: "include",
        headers: { ...enteteTenant(tenantId), ...(t && { Authorization: `Bearer ${t}` }) },
        body: fd,
      });
      if (r.ok) {
        const d = await r.json();
        const sugg = d.suggestions || {};
        setSuggestions(Object.keys(sugg).length ? sugg : {});
        toast.success(Object.keys(sugg).length ? `${Object.keys(sugg).length} suggestion(s) — à vérifier` : "Document déposé (aucune donnée extraite)");
      } else {
        toast.message("Document déposé — extraction IA indisponible, saisie manuelle.");
        setSuggestions({});
      }
    } catch { toast.error("Erreur réseau"); setSuggestions({}); }
    finally { setExtracting(false); }
  };
  // Application NON destructive : ne remplit QUE les champs vides.
  const applySuggestions = () => {
    if (!suggestions) return;
    const s = suggestions;
    setBenef((b) => ({ ...b,
      civilite: b.civilite || s.civilite || "", nom: b.nom || s.nom || "", prenom: b.prenom || s.prenom || "",
      date_naissance: b.date_naissance || s.date_naissance || "", email: b.email || s.email || "", telephone: b.telephone || s.telephone || "" }));
    setLogement((l) => ({ ...l, adresse: l.adresse || s.adresse || "", code_postal: l.code_postal || s.code_postal || "", ville: l.ville || s.ville || "" }));
    setMpa((m) => ({ ...m, rfr: m.rfr || s.rfr || "", nb_personnes_foyer: m.nb_personnes_foyer || s.nb_personnes_foyer || "" }));
    toast.success("Suggestions appliquées aux champs vides — vérifiez puis créez.");
  };

  const num = (v) => (v === "" || v == null ? null : (parseFloat(String(v).replace(",", ".")) || null));

  // Résout/crée le client puis crée le dossier. dedup sur création client (409) + dossier.
  const create = async (forceClient = false, forceDossier = false) => {
    setCreating(true); setError(null);
    try {
      let clientId = existingClient?.id;
      if (clientMode === "nouveau" && !clientId) {
        const body = {
          civilite: benef.civilite || "M.", nom: benef.nom, prenom: benef.prenom,
          email: benef.email, telephone: benef.telephone, date_naissance: benef.date_naissance,
          // 🔴 N°744 : le type CHOISI par l'utilisateur, ⛔ plus « particulier » en dur.
          type_client: typeClient,
          adresse: { rue: logement.adresse || "", code_postal: logement.code_postal || "", ville: logement.ville || "" },
        };
        for (const k of Object.keys(body)) if (body[k] === "" || body[k] == null) delete body[k];
        const r = await fetch(`${API}/api/metier/clients${forceClient ? "?force=true" : ""}`, { method: "POST", headers: headers(), credentials: "include", body: JSON.stringify(body) });
        if (r.status === 409) {
          const err = await r.json().catch(() => ({}));
          const matches = (err?.detail?.candidates || []).map((c) => ({ id: c.client_id, nom: c.nom, prenom: c.prenom, email: c.email || "", adresse: { code_postal: c.code_postal, ville: c.ville }, reference: c.reference, score: c.score, match_reasons: c.match_reasons || [] }));
          setCreating(false);
          setDupDialog({ open: true, matches,
            onUse: async (m) => { setDupDialog({ open: false, matches: [] }); setExistingClient({ id: m.id, nom: m.nom, prenom: m.prenom }); setClientMode("existant"); },
            onCreate: () => { setDupDialog({ open: false, matches: [] }); create(true, forceDossier); } });
          return;
        }
        if (!r.ok) { const e = await r.json().catch(() => ({})); setError(e?.detail || "Erreur création client"); setCreating(false); return; }
        clientId = (await r.json()).id;
      }
      if (!clientId) { setError("Bénéficiaire requis"); setCreating(false); return; }

      // Doublon DOSSIER (client existant déjà engagé)
      if (!forceDossier) {
        try {
          const rd = await fetch(`${API}/api/metier/dossiers/check-duplicate-by-client?client_id=${encodeURIComponent(clientId)}`, { headers: headers(), credentials: "include" });
          const dd = rd.ok ? await rd.json() : { matches: [] };
          if ((dd.matches || []).length) {
            setCreating(false);
            setDupDialog({ open: true, mode: "dossier", matches: dd.matches,
              onUse: (m) => { setDupDialog({ open: false, matches: [] }); onSuccess?.({ id: m.id, reference: m.reference, activite }); },
              onCreate: () => { setDupDialog({ open: false, matches: [] }); create(forceClient, true); } });
            return;
          }
        } catch { /* noop */ }
      }

      // Création du dossier (source unique : adresse_projet + activité + client_id)
      const dossierBody = { activite, client_id: clientId,
        adresse_projet: { rue: logement.adresse || "", code_postal: logement.code_postal || "", ville: logement.ville || "" } };
      const rdos = await fetch(`${API}/api/metier/dossiers`, { method: "POST", headers: headers(), credentials: "include", body: JSON.stringify(dossierBody) });
      if (!rdos.ok) { const e = await rdos.json().catch(() => ({})); setError(e?.detail || "Erreur création dossier"); setCreating(false); return; }
      const dossier = await rdos.json();

      // 🔴 N°744 — LES ADRESSES SUPPLÉMENTAIRES (cas SYNDIC), posées dès la création.
      // ⚠️ `principale: false` : la principale est l'adresse du LOGEMENT, rendue par la
      //    lecture dérivée (ADR-003 temps 1). Le serveur MATÉRIALISE cette dérivée avant
      //    d'ajouter la seconde — la 1re ⛔ ne disparaît donc PAS.
      // ⚠️ Un échec ici ⛔ N'ANNULE PAS la création : le dossier existe, on le DIT et on
      //    laisse l'utilisateur les ressaisir dans l'onglet Administratif. ⛔ Pas de
      //    rollback silencieux qui perdrait le dossier.
      const aPoster = adressesSup.filter((a) => (a.adresse || "").trim());
      if (aPoster.length) {
        const echecs = [];
        for (const a of aPoster) {
          try {
            const ra = await fetch(`${API}/api/metier/dossiers/${dossier.id}/adresses`, {
              method: "POST", headers: headers(), credentials: "include",
              body: JSON.stringify({ ...a, principale: false }),
            });
            if (!ra.ok) echecs.push(a.adresse);
          } catch { echecs.push(a.adresse); }
        }
        if (echecs.length) {
          toast.error(`Dossier créé, mais ${echecs.length} adresse(s) ⛔ non enregistrée(s) : `
                      + `${echecs.join(" · ")}. À ressaisir dans l'onglet Administratif.`);
        } else {
          toast.success(`${aPoster.length} adresse(s) supplémentaire(s) enregistrée(s).`);
        }
      }

      // PATCH des champs LOGEMENT + MPA → routés vers metier_clients (source unique).
      const patch = {
        date_naissance: benef.date_naissance || undefined,
        statut_occupation: logement.statut_occupation || undefined,
        surface_habitable: num(logement.surface_habitable),
        // 🔑 CLI-16 — LES REVENUS PARTENT TOUJOURS (ils servent à MPR) ; les
        //    critères d'ÉLIGIBILITÉ MPA ⛔ ne partent QUE si le parcours porte
        //    l'étape MaPrimeAdapt'.
        // ⚠️ Le test porte sur les ÉTAPES EFFECTIVES du parcours, ⛔ pas sur le
        //    nom du profil : un parcours qui n'affiche PAS le GIR ⛔ ne doit pas
        //    pouvoir en écrire un — sinon l'écran et la base divergent, et c'est
        //    la base qui gagne en silence.
        rfr: num(mpa.rfr), nb_personnes_foyer: num(mpa.nb_personnes_foyer),
        ...(STEPS.some((s) => s.key === "mpa") ? {
          gir: mpa.gir || undefined, age: num(mpa.age),
          beneficiaire_apa: mpa.beneficiaire_apa || undefined,
          beneficiaire_pch: mpa.beneficiaire_pch || undefined,
        } : {}),
        logement: { type_logement: logement.type_logement || undefined, surface_habitable: num(logement.surface_habitable), statut_occupation: logement.statut_occupation || undefined },
      };
      for (const k of Object.keys(patch)) if (patch[k] === undefined) delete patch[k];
      try {
        await fetch(`${API}/api/metier/dossiers/${dossier.id}`, { method: "PATCH", headers: headers(), credentials: "include", body: JSON.stringify(patch) });
      } catch { /* non bloquant : le dossier est créé */ }
      toast.success(`Dossier ${dossier.reference || ""} créé`);
      onSuccess?.(dossier);
    } catch (e) {
      setError("Erreur réseau"); setCreating(false);
    }
  };

  const isLast = step === STEPS.length - 1;
  const cur = STEPS[step]?.key;  // render par clé (profil-agnostique)

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="tef-wizard">
      {/* En-tête + stepper */}
      <div className="flex items-center gap-3">
        {onBack && <Button variant="outline" size="sm" onClick={onBack}><ArrowLeft className="w-4 h-4 mr-1" />Retour</Button>}
        <h2 className="text-lg font-medium" style={{ color: "var(--oh-text)" }}>Nouveau dossier {activite === "tef" ? "TEF (MaPrimeAdapt')" : ""}</h2>
      </div>
      <div className="flex items-center gap-2" data-testid="tef-wizard-stepper">
        {STEPS.map((s, i) => {
          const on = i === step, done = i < step;
          return (
            <div key={s.key} className="flex items-center gap-2 flex-1">
              <button type="button" onClick={() => i <= step && setStep(i)} data-testid={`tef-step-${s.key}`}
                className="flex items-center gap-2 rounded px-3 py-1.5 text-sm w-full"
                style={{ background: on ? "var(--oh-stat-blue-bg)" : "var(--oh-bg-surface)", border: `1px solid ${on ? "var(--oh-stat-blue-border)" : "var(--oh-border)"}`, color: on ? "var(--oh-stat-blue-text)" : "var(--oh-text)", opacity: done ? 0.8 : 1 }}>
                {done ? <Check className="w-4 h-4" /> : <s.icon className="w-4 h-4" />}
                <span className="truncate">{i + 1}. {s.label}</span>
              </button>
            </div>
          );
        })}
      </div>
      <div className="text-xs" data-testid="tef-step-counter" style={lbl}>
        Étape {step + 1}/{STEPS.length}{emptyCount(step) > 0 ? ` — ${emptyCount(step)} champ(s) à remplir (complétez maintenant ou plus tard)` : " — complet"}
      </div>

      <div className="rounded-lg p-4" style={{ background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)" }}>
        {cur === "beneficiaire" && (
          <div className="space-y-3">
            <div className="flex gap-2">
              {[["nouveau", "Nouveau bénéficiaire"], ["existant", "Bénéficiaire existant"]].map(([m, t]) => (
                <button key={m} type="button" onClick={() => setClientMode(m)} data-testid={`tef-client-${m}`}
                  className="rounded px-3 py-2 text-sm" style={{ background: clientMode === m ? "var(--oh-stat-blue-bg)" : "var(--oh-bg-surface)", border: `1px solid ${clientMode === m ? "var(--oh-stat-blue-border)" : "var(--oh-border)"}`, color: clientMode === m ? "var(--oh-stat-blue-text)" : "var(--oh-text)" }}>{t}</button>
              ))}
            </div>
            {clientMode === "existant" ? (
              <div className="space-y-2">
                <Txt label="Rechercher un client (nom)" value={search} onChange={doSearch} placeholder="Taper un nom…" testId="tef-client-search" />
                {existingClient && <div className="text-sm rounded px-3 py-2" style={{ background: "var(--oh-stat-green-bg)", color: "var(--oh-stat-green-text)" }}>Sélectionné : <b>{existingClient.nom} {existingClient.prenom}</b></div>}
                {results.map((c) => (
                  <button key={c.id} type="button" onClick={() => { setExistingClient(c); setResults([]); setSearch(`${c.nom} ${c.prenom || ""}`); }}
                    data-testid="tef-client-result" className="w-full text-left rounded px-3 py-2 text-sm" style={inputStyle}>
                    {c.nom} {c.prenom} {c.reference ? `· ${c.reference}` : ""}
                  </button>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Sel label="Civilité" value={benef.civilite} onChange={(v) => setBenef({ ...benef, civilite: v })}
                  options={(civiliteOptions.length ? civiliteOptions.map((c) => ({ value: c.id, label: c.label })) : [{ value: "M.", label: "M." }, { value: "Mme", label: "Mme" }])} testId="tef-civilite" />
                <Txt label="Nom *" value={benef.nom} onChange={(v) => setBenef({ ...benef, nom: v })} testId="tef-nom" />
                <Txt label="Prénom" value={benef.prenom} onChange={(v) => setBenef({ ...benef, prenom: v })} testId="tef-prenom" />
                <Txt label="Date de naissance" type="date" value={benef.date_naissance} onChange={(v) => setBenef({ ...benef, date_naissance: v })} testId="tef-naissance" />
                <Txt label="Email" type="email" value={benef.email} onChange={(v) => setBenef({ ...benef, email: v })} testId="tef-email" />
                <Txt label="Téléphone" value={benef.telephone} onChange={(v) => setBenef({ ...benef, telephone: v })} testId="tef-tel" />
                {/* 🔴 N°744 — LE TYPE DE CLIENT, CHOISI ICI. Un syndic ⛔ n'est PAS un
                    particulier : c'est lui qui gère PLUSIEURS adresses (ADR-003). */}
                <Sel label="Type de client" value={typeClient} onChange={setTypeClient}
                  options={typeClientOptions} testId="tef-type-client" />
              </div>
            )}
          </div>
        )}

        {cur === "logement" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="text-xs mb-1 block" style={lbl}>Adresse du logement (autocomplétée)</label>
              <BanAddressInput value={logement.adresse} testId="tef-adresse"
                onChange={(v) => setLogement((l) => ({ ...l, adresse: v }))}
                onPick={({ rue, cp, ville }) => setLogement((l) => ({ ...l, adresse: rue, code_postal: cp || l.code_postal, ville: ville || l.ville }))} />
            </div>
            <Txt label="Code postal" value={logement.code_postal} onChange={(v) => setLogement({ ...logement, code_postal: v })} testId="tef-cp" />
            <Txt label="Ville" value={logement.ville} onChange={(v) => setLogement({ ...logement, ville: v })} testId="tef-ville" />
            <Sel label="Type de logement" value={logement.type_logement} onChange={(v) => setLogement({ ...logement, type_logement: v })}
              options={[{ value: "maison", label: "Maison" }, { value: "appartement", label: "Appartement" }]} testId="tef-type-logement" />
            <Txt label="Surface habitable (m²)" type="number" value={logement.surface_habitable} onChange={(v) => setLogement({ ...logement, surface_habitable: v })} testId="tef-surface" />
            <Sel label="Statut d'occupation" value={logement.statut_occupation} onChange={(v) => setLogement({ ...logement, statut_occupation: v })}
              options={[{ value: "proprietaire_occupant", label: "Propriétaire occupant" }, { value: "locataire", label: "Locataire" }, { value: "proprietaire_bailleur", label: "Propriétaire bailleur" }]} testId="tef-occupation" />

            {/* 🔴 N°744 — ADRESSES SUPPLÉMENTAIRES DÈS LA CRÉATION (cas SYNDIC).
                ⚠️ L'adresse ci-dessus reste la PRINCIPALE : ⛔ on ⛔ ne la ressaisit PAS ici.
                ⚠️ Rendu pour tous les types (un bailleur aussi a plusieurs lots) : ⛔ aucun
                   gate sur `typeClient`, qui reste modifiable après. */}
            <div className="sm:col-span-2 rounded-lg p-3" style={{ background: "var(--oh-bg-surface)", border: "0.5px solid var(--oh-border)" }} data-testid="tef-adresses-sup">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs" style={lbl}>
                  Autres adresses d'intervention {adressesSup.length ? `(${adressesSup.length})` : ""}
                </span>
                <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                  style={{ color: "var(--oh-accent-text)" }} data-testid="tef-adresse-ajouter"
                  onClick={() => setAdressesSup((l) => [...l, { libelle: "", adresse: "", code_postal: "", ville: "" }])}>
                  + Ajouter une adresse
                </Button>
              </div>
              {adressesSup.length === 0 && (
                <div className="text-xs" style={{ color: "var(--oh-text-muted)" }}>
                  Un syndic ou un bailleur intervient sur plusieurs bâtiments : ajoutez-les ici.
                </div>
              )}
              {adressesSup.map((a, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-2" data-testid={`tef-adresse-sup-${i}`}>
                  <BanAddressInput value={a.adresse} testId={`tef-adresse-sup-rue-${i}`}
                    onChange={(v) => setAdressesSup((l) => l.map((x, j) => j === i ? { ...x, adresse: v } : x))}
                    onPick={({ rue, cp, ville }) => setAdressesSup((l) => l.map((x, j) => j === i
                      ? { ...x, adresse: rue, code_postal: cp || x.code_postal, ville: ville || x.ville } : x))} />
                  <Txt label="Code postal" value={a.code_postal} testId={`tef-adresse-sup-cp-${i}`}
                    onChange={(v) => setAdressesSup((l) => l.map((x, j) => j === i ? { ...x, code_postal: v } : x))} />
                  <Txt label="Ville" value={a.ville} testId={`tef-adresse-sup-ville-${i}`}
                    onChange={(v) => setAdressesSup((l) => l.map((x, j) => j === i ? { ...x, ville: v } : x))} />
                  <div className="flex items-end gap-2">
                    <Txt label="Libellé" value={a.libelle} testId={`tef-adresse-sup-libelle-${i}`}
                      onChange={(v) => setAdressesSup((l) => l.map((x, j) => j === i ? { ...x, libelle: v } : x))} />
                    <Button size="sm" variant="ghost" className="h-9 px-2 text-xs"
                      aria-label={`Retirer l'adresse ${i + 1}`} data-testid={`tef-adresse-retirer-${i}`}
                      onClick={() => setAdressesSup((l) => l.filter((_, j) => j !== i))}>✕</Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {cur === "mpa" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Sel label="GIR (autonomie)" value={mpa.gir} onChange={(v) => setMpa({ ...mpa, gir: v })}
              options={[1, 2, 3, 4, 5, 6].map((g) => ({ value: String(g), label: `GIR ${g}` }))} testId="tef-gir" />
            <Txt label="Âge" type="number" value={mpa.age} onChange={(v) => setMpa({ ...mpa, age: v })} testId="tef-age" />
            <Txt label="Revenu fiscal de référence (RFR)" type="number" value={mpa.rfr} onChange={(v) => setMpa({ ...mpa, rfr: v })} testId="tef-rfr" />
            <Txt label="Nombre de personnes du foyer" type="number" value={mpa.nb_personnes_foyer} onChange={(v) => setMpa({ ...mpa, nb_personnes_foyer: v })} testId="tef-foyer" />
            <label className="flex items-center gap-2 text-sm pt-6" style={{ color: "var(--oh-text)" }}>
              <input type="checkbox" checked={mpa.beneficiaire_apa} onChange={(e) => setMpa({ ...mpa, beneficiaire_apa: e.target.checked })} data-testid="tef-apa" /> Bénéficiaire APA
            </label>
            <label className="flex items-center gap-2 text-sm pt-6" style={{ color: "var(--oh-text)" }}>
              <input type="checkbox" checked={mpa.beneficiaire_pch} onChange={(e) => setMpa({ ...mpa, beneficiaire_pch: e.target.checked })} data-testid="tef-pch" /> Bénéficiaire PCH
            </label>
          </div>
        )}

        {/* 🔑 CLI-16 — REVENUS SEULS. ⛔ NI GIR, ⛔ NI âge, ⛔ NI APA, ⛔ NI PCH :
            ce sont des critères d'éligibilité MaPrimeAdapt', ⛔ pas des revenus.
            ⚠️ L'état réutilisé est `mpa` — ⛔ pas un second état parallèle : deux
            états pour le même RFR divergeraient, et c'est la charge de création
            qui déciderait lequel part en base. */}
        {cur === "revenus" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Txt label="Revenu fiscal de référence (RFR)" type="number" value={mpa.rfr}
              onChange={(v) => setMpa({ ...mpa, rfr: v })} testId="b2c-rfr" />
            <Txt label="Nombre de personnes du foyer" type="number" value={mpa.nb_personnes_foyer}
              onChange={(v) => setMpa({ ...mpa, nb_personnes_foyer: v })} testId="b2c-foyer" />
            <p className="text-xs sm:col-span-2" style={lbl}>
              Ces informations serviront au calcul des aides. Elles sont complétables plus tard.
            </p>
          </div>
        )}

        {cur === "documents" && (
          <div className="space-y-3">
            <div className="rounded p-3" style={{ background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)" }}>
              <div className="flex items-center gap-2 mb-2" style={{ color: "var(--oh-text)" }}>
                <Sparkles className="w-4 h-4" /> <b className="text-sm">Déposer un document et remplir (IA)</b>
              </div>
              <p className="text-xs mb-2" style={lbl}>CNI → identité · avis d'imposition → RFR. L'IA SUGGÈRE, vous validez (rien n'est écrit sans votre accord).</p>
              <label className="inline-flex items-center gap-2 rounded px-3 py-2 text-sm cursor-pointer" style={inputStyle} data-testid="tef-upload-label">
                <Upload className="w-4 h-4" /> {docName || "Choisir un document (PDF/JPG/PNG)"}
                <input type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" data-testid="tef-upload-input"
                  onChange={(e) => onUploadDoc(e.target.files?.[0])} />
              </label>
              {extracting && <div className="text-xs mt-2" style={lbl}><Loader2 className="w-3 h-3 inline animate-spin mr-1" />Extraction en cours…</div>}
              {suggestions && Object.keys(suggestions).length > 0 && (
                <div className="mt-3" data-testid="tef-suggestions">
                  <div className="text-xs mb-1" style={lbl}>Suggestions IA (à vérifier) :</div>
                  <div className="flex flex-wrap gap-1 mb-2">
                    {Object.entries(suggestions).map(([k, v]) => (
                      <span key={k} className="text-xs rounded px-2 py-0.5" style={{ background: "var(--oh-stat-purple-bg)", color: "var(--oh-stat-purple-text)" }}>{k}: {String(v)}</span>
                    ))}
                  </div>
                  <Button size="sm" variant="outline" onClick={applySuggestions} data-testid="tef-apply-suggestions">Appliquer aux champs vides</Button>
                </div>
              )}
            </div>
            {/* Récapitulatif */}
            <div className="text-sm rounded p-3" style={{ background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)", color: "var(--oh-text)" }}>
              <div className="font-medium mb-1">Récapitulatif</div>
              <div style={lbl} className="text-xs">Bénéficiaire : {existingClient ? `${existingClient.nom} ${existingClient.prenom || ""}` : `${benef.civilite} ${benef.nom} ${benef.prenom}`}</div>
              <div style={lbl} className="text-xs">Logement : {[logement.adresse, logement.code_postal, logement.ville].filter(Boolean).join(", ") || "—"}{logement.surface_habitable ? ` · ${logement.surface_habitable} m²` : ""}</div>
              <div style={lbl} className="text-xs">MPA : {mpa.gir ? `GIR ${mpa.gir}` : "GIR —"}{mpa.rfr ? ` · RFR ${mpa.rfr}` : ""}{mpa.nb_personnes_foyer ? ` · ${mpa.nb_personnes_foyer} pers.` : ""}</div>
            </div>
          </div>
        )}
      </div>

      {error && <div className="text-sm rounded px-3 py-2" style={{ background: "var(--oh-stat-red-bg)", color: "var(--oh-stat-red-text)" }}>{error}</div>}

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}><ArrowLeft className="w-4 h-4 mr-1" />Précédent</Button>
        <div className="flex flex-col items-end gap-1">
          {!canNext && <span className="text-xs" data-testid="tef-blocked-msg" style={{ color: "var(--oh-stat-amber-text)" }}>Obligatoire : {missing.join(", ")}</span>}
          {!isLast ? (
            <Button onClick={() => canNext && setStep((s) => Math.min(STEPS.length - 1, s + 1))} disabled={!canNext} data-testid="tef-next">Suivant<ArrowRight className="w-4 h-4 ml-1" /></Button>
          ) : (
            <Button onClick={() => create()} disabled={creating || !canNext} className="bg-emerald-600 hover:bg-emerald-700" data-testid="tef-create">
              {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Créer le dossier
            </Button>
          )}
        </div>
      </div>

      <ClientDuplicateDialog
        open={dupDialog.open} mode={dupDialog.mode || "client"} matches={dupDialog.matches}
        onUseExisting={dupDialog.onUse} onCreateAnyway={dupDialog.onCreate} onCancel={() => setDupDialog({ open: false, matches: [] })} />
    </div>
  );
}
